import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiCreateTicket, apiListTickets } from '../services/api';
import { SHOW_CONTACT_CHANNELS } from '../constants/featureFlags';
import { APP_NAME, CONTACT_EMAIL, EDITEUR, LEGAL_VERSION } from '../constants/site';
import {
  TICKET_CATEGORIES,
  TICKET_CATEGORY_LABELS,
  TICKET_STATUS_LABELS,
  formatTicketDate,
} from '../constants/support';
import './Legal.css';

const EMPTY_FORM = { categorie: 'question', sujet: '', message: '' };

/**
 * Formulaire de contact + suivi de mes demandes.
 *
 * Rendu uniquement pour un compte connecté : `/legal/contact` reste accessible
 * sans session (la politique de confidentialité y renvoie), et un ticket exige
 * un auteur. Un visiteur déconnecté voit l'adresse e-mail, plus bas sur la page.
 * Les rôles admin et éditeur sont exclus côté serveur (403) : leur afficher le
 * formulaire ne mènerait qu'à une erreur.
 */
const SupportTickets = () => {
  const navigate = useNavigate();
  const { getToken, user } = useAuth();
  const [tickets, setTickets] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const canOpenTicket = Boolean(user) && ['sportive', 'coach'].includes(user.role);

  const load = useCallback(() => {
    if (!canOpenTicket) return;
    apiListTickets(getToken())
      .then(setTickets)
      .catch((err) => setError(err.message));
  }, [getToken, canOpenTicket]);

  useEffect(() => { load(); }, [load]);

  if (!canOpenTicket) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setSending(true);
    try {
      await apiCreateTicket({
        categorie: form.categorie,
        sujet: form.sujet.trim(),
        message: form.message.trim(),
      }, getToken());
      setForm(EMPTY_FORM);
      setMessage('Votre demande a bien été enregistrée. Nous vous répondons ici même.');
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <div className="legal-page__card">
        <div className="legal-page__section">
          <h2 className="legal-page__section-title">Écrire au support</h2>
          <p className="legal-page__section-text">
            Votre message reste dans l'application : rien ne transite par une boîte mail.
            Toute demande relative à vos données est traitée dans un délai d'un mois
            (Art.&nbsp;12 RGPD).
          </p>

          {error && <p className="legal-page__form-error">{error}</p>}
          {message && <p className="legal-page__form-success">{message}</p>}

          <form className="legal-page__form" onSubmit={handleSubmit}>
            <label className="legal-page__form-label" htmlFor="support-categorie">
              Motif
            </label>
            <select
              id="support-categorie"
              className="legal-page__form-input"
              value={form.categorie}
              onChange={(e) => setForm({ ...form, categorie: e.target.value })}
            >
              {TICKET_CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>

            <label className="legal-page__form-label" htmlFor="support-sujet">Sujet</label>
            <input
              id="support-sujet"
              className="legal-page__form-input"
              type="text"
              value={form.sujet}
              required
              minLength={3}
              maxLength={150}
              onChange={(e) => setForm({ ...form, sujet: e.target.value })}
            />

            <label className="legal-page__form-label" htmlFor="support-message">Message</label>
            <textarea
              id="support-message"
              className="legal-page__form-textarea"
              rows={5}
              value={form.message}
              required
              maxLength={5000}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
            />

            <button className="legal-page__form-submit" type="submit" disabled={sending}>
              {sending ? 'Envoi…' : 'Envoyer ma demande'}
            </button>
          </form>
        </div>
      </div>

      {tickets && tickets.length > 0 && (
        <div className="legal-page__card">
          <div className="legal-page__section">
            <h2 className="legal-page__section-title">Mes demandes</h2>
          </div>
          {tickets.map((t) => (
            <button
              key={t.id}
              type="button"
              className="legal-page__contact-row legal-page__ticket-row"
              onClick={() => navigate(`/support/ticket/${t.id}`)}
            >
              <div className="legal-page__contact-info">
                <p className="legal-page__contact-label">{t.sujet}</p>
                <p className="legal-page__contact-sublabel">
                  {TICKET_CATEGORY_LABELS[t.categorie] || t.categorie}
                  {' · '}
                  {TICKET_STATUS_LABELS[t.statut] || t.statut}
                  {' · '}
                  {formatTicketDate(t.updated_at)}
                  {t.messages_count > 1 ? ` · ${t.messages_count} messages` : ''}
                </p>
              </div>
              <span className="legal-page__contact-arrow">›</span>
            </button>
          ))}
        </div>
      )}
    </>
  );
};

const ContactSupport = () => {
  const navigate = useNavigate();

  return (
    <div className="legal-page">
      <div className="legal-page__header">
        <button className="legal-page__back-btn" onClick={() => navigate('/profil')}>
          <svg viewBox="0 0 24 24" fill="none" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        <h1 className="legal-page__title">Contact & Support</h1>
      </div>

      {/* Canal principal : le formulaire ci-dessous. L'adresse e-mail et l'adresse
          postale restent affichées après lui — la politique de confidentialité y
          renvoie, et elles restent le seul recours pour qui ne peut plus se
          connecter. Ne pas les masquer derrière un feature flag. */}
      <SupportTickets />

      <div className="legal-page__card">
        <div className="legal-page__section">
          <h2 className="legal-page__section-title">Autres moyens de nous joindre</h2>
          <p className="legal-page__section-text">
            Si vous ne parvenez plus à vous connecter, écrivez-nous depuis l'adresse e-mail
            de votre compte à <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
          </p>
          <p className="legal-page__section-text">
            Par courrier : {EDITEUR.nom} — {APP_NAME}, à l'attention du référent protection des
            données — {EDITEUR.siege}. Toute demande relative à vos
            données est traitée dans un délai d'un mois.
          </p>
        </div>
      </div>

      {/* Canaux additionnels (chat, FAQ) — masqués tant que non fonctionnels (SHOW_CONTACT_CHANNELS) */}
      {SHOW_CONTACT_CHANNELS && (
      <div className="legal-page__card">
        <span className="legal-page__placeholder-badge">💬 À compléter</span>

        <div className="legal-page__contact-row">
          <div className="legal-page__contact-icon legal-page__contact-icon--email">✉️</div>
          <div className="legal-page__contact-info">
            <p className="legal-page__contact-label">Email</p>
            <p className="legal-page__contact-sublabel">{CONTACT_EMAIL}</p>
          </div>
          <span className="legal-page__contact-arrow">›</span>
        </div>

        <div className="legal-page__contact-row">
          <div className="legal-page__contact-icon legal-page__contact-icon--chat">💬</div>
          <div className="legal-page__contact-info">
            <p className="legal-page__contact-label">Chat en direct</p>
            <p className="legal-page__contact-sublabel">Disponible lun–ven, 9h–18h</p>
          </div>
          <span className="legal-page__contact-arrow">›</span>
        </div>

        <div className="legal-page__contact-row">
          <div className="legal-page__contact-icon legal-page__contact-icon--faq">❓</div>
          <div className="legal-page__contact-info">
            <p className="legal-page__contact-label">FAQ</p>
            <p className="legal-page__contact-sublabel">Consulter les questions fréquentes</p>
          </div>
          <span className="legal-page__contact-arrow">›</span>
        </div>
      </div>
      )}

      {/* Questions fréquentes */}
      <div className="legal-page__card">
        <div className="legal-page__section">
          <h2 className="legal-page__section-title">Questions fréquentes</h2>
        </div>

        <div className="legal-page__section">
          <h2 className="legal-page__section-title">Comment modifier mes données personnelles ?</h2>
          <p className="legal-page__section-text">
            Rendez-vous dans votre profil, section "Informations privées", puis appuyez sur l'icône de modification.
          </p>
        </div>

        <div className="legal-page__divider" />

        <div className="legal-page__section">
          <h2 className="legal-page__section-title">Comment supprimer mon compte ?</h2>
          <p className="legal-page__section-text">
            Depuis votre profil, tout en bas, bouton « Demander la suppression de mon compte ».
            Vous pouvez aussi ouvrir une demande ci-dessus avec le motif « Suppression de mon
            compte ». Elle est traitée dans un délai d'un mois (Art.&nbsp;12 RGPD).
          </p>
        </div>

        <div className="legal-page__divider" />

        <div className="legal-page__section">
          <h2 className="legal-page__section-title">Combien de temps mes données sont-elles conservées ?</h2>
          <p className="legal-page__section-text">
            Vos données de santé et de profil sont supprimées 6 mois après votre dernière saisie, ou
            dès que vous retirez votre consentement. Les données de compte sont conservées tant que
            votre compte est actif. Le détail figure dans la politique de confidentialité.
          </p>
        </div>

        <div className="legal-page__divider" />

        <div className="legal-page__section">
          <h2 className="legal-page__section-title">Mes données sont-elles partagées ?</h2>
          <p className="legal-page__section-text">
            Vos données ne sont partagées qu'avec votre coach si vous avez activé l'option de partage dans votre profil. Elles ne sont jamais vendues à des tiers.
          </p>
        </div>
      </div>

      <p className="legal-page__version">Version {LEGAL_VERSION} — CycleGala</p>
    </div>
  );
};

export default ContactSupport;
